import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const [attempts, setAttempts] = useState(0);
  const [err, setErr] = useState("");
  const timerRef = useRef(null);

  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); setErr("Missing session id"); return; }
    let cancelled = false;
    let n = 0;
    const poll = async () => {
      if (cancelled) return;
      n += 1;
      setAttempts(n);
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch (e) { setErr(errMsg(e)); }
      if (n >= 15) { setStatus("timeout"); return; }
      timerRef.current = setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center hero-gradient px-4" data-testid="payment-success-page">
      <div className="bg-card border border-border rounded-2xl p-8 w-full max-w-md shadow-sm text-center">
        {status === "polling" && (<>
          <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" />
          <h1 className="text-xl font-display font-semibold mt-4">Confirming your subscription…</h1>
          <p className="text-sm text-muted-foreground mt-2">Stripe is finishing up. Attempt {attempts} of 15.</p>
        </>)}
        {status === "paid" && (<>
          <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500" />
          <h1 className="text-xl font-display font-semibold mt-4" data-testid="payment-success-title">You're all set!</h1>
          <p className="text-sm text-muted-foreground mt-2">Your subscription is active. Thanks for upgrading.</p>
          <Button className="mt-6 w-full" onClick={() => navigate("/dashboard")} data-testid="payment-success-continue">Go to the dashboard</Button>
        </>)}
        {status === "failed" && (<>
          <XCircle className="h-10 w-10 mx-auto text-rose-500" />
          <h1 className="text-xl font-display font-semibold mt-4">Payment didn't complete</h1>
          <p className="text-sm text-muted-foreground mt-2">{err || "Please try again or pick a different plan."}</p>
          <Button className="mt-6 w-full" onClick={() => navigate("/settings?tab=billing")} data-testid="payment-success-retry">Back to billing</Button>
        </>)}
        {status === "timeout" && (<>
          <Loader2 className="h-10 w-10 mx-auto text-muted-foreground" />
          <h1 className="text-xl font-display font-semibold mt-4">Still processing</h1>
          <p className="text-sm text-muted-foreground mt-2">This usually finishes in a moment. We'll keep syncing in the background — check your Settings in a minute.</p>
          <Button className="mt-6 w-full" onClick={() => navigate("/settings?tab=billing")} data-testid="payment-success-timeout-continue">Open billing</Button>
        </>)}
      </div>
    </div>
  );
}
